// Insights → Behavior: tilt, greed, fear, FOMO and overtrading, each with what the trades show
// (behaviorDetect.js) next to what the member reported (End-of-day emotions, Live session reset
// buttons). Also the note in the trade window and the Live session warning.
import { useEffect, useMemo, useState } from 'react';
import { GROUPS, PATTERNS, detectBehavior, ruleFor } from './behaviorDetect';
import { dayKey, formatDuration, formatMoney, netPnl, tradingDayKey } from './pnl';
import { currentLocale } from './langState';
import { useI18n } from './i18n';

const ICONS = {
  revenge: '⚡', sizeUp: '📈', streak: '🔁', pastLimit: '🛑', giveBack: '💸', winSizeUp: '🎰',
  cutWinners: '✂️', unplanned: '🏃', overtrading: '🔥', offHours: '🌙',
};
const SIGNALS = {
  strong: ['Strong signal', 'Your trades show this clearly: it’s in the times and sizes.'],
  good: ['Good signal', 'Your trades show this well, from your P&L, hold times or trade count.'],
  weak: ['Weaker signal', 'Trades alone show this only in part. What you report matters most here.'],
};
const tone = (n) => (n > 0 ? 'gain' : n < 0 ? 'loss' : '');
const dayText = (key) => new Date(`${key}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: 'short', month: 'short', day: 'numeric' });
const patternOf = (kind) => PATTERNS.find((p) => p.kind === kind);
const groupOf = (kind) => GROUPS.find((g) => g.id === patternOf(kind).group);
const asRow = (key, label, trades) => {
  const nets = trades.map(netPnl);
  const net = nets.reduce((a, b) => a + b, 0);
  return { key, label, trades, count: trades.length, net, winRate: trades.length ? (nets.filter((n) => n > 0).length / trades.length) * 100 : 0 };
};

// What the member reported for one behavior.
function Reported({ g }) {
  const { t } = useI18n();
  const r = g.reported;
  if (!r.days) {
    return (
      <div className="bh-side">
        <span className="bh-side-label">{t('You reported it')}</span>
        <p className="field-hint">{t('Not yet. Check it in your End-of-day reflection or tap its reset button in Live session when you feel it.')}</p>
      </div>
    );
  }
  return (
    <div className="bh-side">
      <span className="bh-side-label">{t('You reported it')}</span>
      <p>
        <strong>{t(r.days === 1 ? '{n} day' : '{n} days', { n: r.days })}</strong>
        {r.taps > 0 && <> &bull; {t(r.taps === 1 ? '{n} reset tap' : '{n} reset taps', { n: r.taps })}</>}
      </p>
      {r.tradeDays > 0 && r.otherDays > 0 && (
        <p className="field-hint">
          {t('Those days averaged {avg}, vs {other} on your other trading days.', { avg: formatMoney(r.avg), other: formatMoney(r.otherAvg) })}
        </p>
      )}
    </div>
  );
}

// `onShowTrades(row)` opens the Insights trade list ({ label, trades, count, net, winRate });
// `onAddRule(text)` adds a rule to the member's routine and resolves 'added' or 'exists'.
export default function BehaviorInsights({ trades, accounts, notes, onShowTrades, onAddRule }) {
  const { t, lang } = useI18n();
  const r = useMemo(() => detectBehavior(trades, { accounts, notes }), [trades, accounts, notes, lang]); // eslint-disable-line react-hooks/exhaustive-deps
  const [added, setAdded] = useState({});
  const share = r.timedCount ? Math.round((r.flagged.count / r.timedCount) * 100) : 0;
  const active = r.groups.filter((g) => g.patterns.some((p) => p.count) || g.reported.days);
  const quiet = r.groups.filter((g) => !active.includes(g));
  const holds = r.baseline.holds;

  async function addRule(kind) {
    try {
      const result = await onAddRule(ruleFor(kind, r.baseline));
      setAdded((a) => ({ ...a, [kind]: result === 'exists' ? 'exists' : 'added' }));
    } catch {
      setAdded((a) => ({ ...a, [kind]: 'error' }));
    }
  }

  if (!r.timedCount) {
    return <p className="empty">{t('Behavior checks need trades with entry times. Files that only give the day (like Schwab’s) can’t show them.')}</p>;
  }

  return (
    <div className="bh">
      <section className={`bh-head ${r.flagged.count ? (r.flagged.net < 0 ? 'bad' : 'mixed') : 'good'}`} aria-labelledby="bh-title">
        <p className="eyebrow">{t('Behavior')}</p>
        {r.flagged.count ? (
          <>
            <h2 id="bh-title">
              {r.flagged.net < 0
                ? <>{t('Your trading behavior cost you')} <span className="loss">{formatMoney(r.flagged.net)}</span></>
                : <>{t('Your flagged trades made')} <span className="gain">{formatMoney(r.flagged.net)}</span>{t(', this time')}</>}
            </h2>
            <p>
              {t('{n} of your {total} trades ({pct}%), on {days} days, match a pattern below.', { n: r.flagged.count, total: r.timedCount, pct: share, days: r.flagged.days })}{' '}
              {t('They averaged {flagged} a trade. Your other trades averaged {clean}.', { flagged: formatMoney(r.flagged.avg), clean: formatMoney(r.clean.avg) })}
            </p>
            {r.flagged.net > 0 && <p className="field-hint">{t('Making money this way is luck you can’t count on. One bad day can undo weeks of good trading.')}</p>}
          </>
        ) : (
          <>
            <h2 id="bh-title">{t('No costly patterns in these trades')} 🎯</h2>
            <p>{t('No revenge trades, no giving back your profits, no overtrading. That discipline is what keeps prop accounts alive.')}</p>
          </>
        )}
        <p className="bh-normal">
          <strong>{t('Your normal:')}</strong>{' '}
          {t(Math.round(r.baseline.tradesPerDay) === 1 ? 'about {n} trade a day' : 'about {n} trades a day', { n: Math.max(1, Math.round(r.baseline.tradesPerDay)) })}
          {r.baseline.hasHours && <>, {t('mostly {hours} New York time', { hours: r.baseline.usualHours })}</>}
          {holds && <>; {t('winners held {win}, losers {loss}', { win: formatDuration(holds.win), loss: formatDuration(holds.loss) })}</>}.{' '}
          {t('Copy trades placed in several accounts at once count as one trade here.')}
        </p>
      </section>

      {active.map((g) => {
        const found = g.patterns.filter((p) => p.count > 0);
        const [signal, signalHelp] = SIGNALS[g.signal];
        return (
          <section key={g.id} className="bh-group" aria-labelledby={`bh-${g.id}`}>
            <header className="bh-group-head">
              <h2 id={`bh-${g.id}`}><span aria-hidden="true">{g.face}</span> {t(g.name)}</h2>
              <span className={`bh-signal ${g.signal}`} title={t(signalHelp)}>{t(signal)}</span>
            </header>
            <div className="bh-sides">
              <div className="bh-side">
                <span className="bh-side-label">{t('Detected from your trades')}</span>
                {g.detected.count ? (
                  <p>
                    <strong className={tone(g.detected.net)}>{formatMoney(g.detected.net)}</strong> &bull; {t(g.detected.count === 1 ? '{n} trade' : '{n} trades', { n: g.detected.count })} &bull; {t(g.detected.days === 1 ? '{n} day' : '{n} days', { n: g.detected.days })}
                  </p>
                ) : found.length ? (
                  <p>{t(found[0].count === 1 ? '{n} quick win, not counted as a cost' : '{n} quick wins, not counted as a cost', { n: found[0].count })}</p>
                ) : (
                  <p className="field-hint">{g.id === 'fomo' && !r.baseline.hasIdeas
                    ? t('Write trade ideas in Morning prep or Live session, and trades outside them will show here.')
                    : t('Nothing in these trades.')}</p>
                )}
              </div>
              <Reported g={g} />
            </div>
            {g.overlap > 0 && (
              <p className="bh-agree">
                {t(g.overlap === 1 ? 'On {n} day you reported {name}, your trades show it too. That’s the strongest sign to work on.' : 'On {n} days you reported {name}, your trades show it too. That’s the strongest sign to work on.', { n: g.overlap, name: t(g.name) })}
              </p>
            )}
            {found.length > 0 && (
              <div className="bh-grid">
                {found.map((p) => (
                  <article key={p.kind} className="bh-card">
                    <header>
                      <span className="bh-icon" aria-hidden="true">{ICONS[p.kind]}</span>
                      <h3>{t(p.name)}</h3>
                    </header>
                    {p.kind === 'cutWinners' && holds ? (
                      <>
                        <p className="bh-net">{t(p.count === 1 ? '{n} quick win' : '{n} quick wins', { n: p.count })}</p>
                        <p className="bh-compare">
                          {t('Winners: held {win}, {avgWin} on average. Losers: held {loss}, {avgLoss} on average.', {
                            win: formatDuration(holds.win), avgWin: formatMoney(holds.avgWin), loss: formatDuration(holds.loss), avgLoss: formatMoney(holds.avgLoss),
                          })}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className={`bh-net ${tone(p.net)}`}>{formatMoney(p.net)}</p>
                        <p className="bh-count">
                          {t(p.count === 1 ? '{n} trade' : '{n} trades', { n: p.count })} &bull; {t(p.days === 1 ? '{n} day' : '{n} days', { n: p.days })} &bull; {t('{rate}% win rate', { rate: Math.round(p.winRate) })}
                        </p>
                        <p className="bh-compare">{t('{avg} a trade, vs {clean} on your other trades.', { avg: formatMoney(p.avg), clean: formatMoney(r.clean.avg) })}</p>
                      </>
                    )}
                    <p className="field-hint">{t(p.what)}</p>
                    <div className="bh-actions">
                      <button type="button" className="secondary small" onClick={() => onShowTrades(asRow(p.kind, t(p.name), p.trades))}>
                        {t(p.count === 1 ? 'See the trade' : 'See the {n} trades', { n: p.count })}
                      </button>
                      {onAddRule && (added[p.kind] ? (
                        <span className="bh-added">{added[p.kind] === 'error' ? t('Could not save') : added[p.kind] === 'exists' ? t('Already in your rules') : t('Added to your rules ✓')}</span>
                      ) : (
                        <button type="button" className="link-button" onClick={() => addRule(p.kind)}>
                          {t('Add the rule: “{rule}”', { rule: ruleFor(p.kind, r.baseline) })}
                        </button>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        );
      })}

      {r.worstDays.length > 0 && (
        <section className="insight-panel wide bh-days">
          <h2>{t('Your costliest behavior days')}</h2>
          <p className="insight-panel-sub">{t('Days with flagged trades, the costliest first. Click a day to see what happened.')}</p>
          <ul>
            {r.worstDays.slice(0, 8).map((d) => (
              <li key={d.day}>
                <button type="button" className="bh-day" onClick={() => onShowTrades(asRow(d.day, dayText(d.day), d.trades))}>
                  <span className="bh-day-date">{dayText(d.day)}</span>
                  <span className="bh-day-kinds">{d.kinds.map((k) => <span key={k} className="bh-chip">{ICONS[k]} {t(patternOf(k).name)}</span>)}</span>
                  <span className="bh-day-nums">
                    <span className={tone(d.flaggedNet)}>{formatMoney(d.flaggedNet)}</span>
                    <small>{t('{n} flagged · day {total}', { n: d.trades.length, total: formatMoney(d.dayNet) })}</small>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {quiet.length > 0 && (
        <section className="bh-clean">
          <h2>{t('Not showing up')}</h2>
          <ul>{quiet.map((g) => <li key={g.id}><span aria-hidden="true">✓</span> {g.face} {t(g.name)}</li>)}</ul>
          {!r.baseline.hasHours && <p className="field-hint">{t('Off-hours trades show once you have 20 trades with times, so your usual hours are clear.')}</p>}
          {!holds && <p className="field-hint">{t('Cutting winners short shows once you have 8 winners and 8 losers with exit times.')}</p>}
          {!r.baseline.hasIdeas && <p className="field-hint">{t('Trades outside your plan show once you write trade ideas.')}</p>}
          {!accounts.some((a) => a.dailyLossLimit > 0) && <p className="field-hint">{t('Set a daily loss limit on your accounts to see trades taken past it.')}</p>}
        </section>
      )}

      <p className="insight-caution">
        {t('These flags come from the times, sizes and results of your trades, not from how you felt. A quick re-entry or a fast exit can be part of a plan; if it was, ignore the flag.')}
      </p>
    </div>
  );
}

// Why a trade was flagged, for the trade window.
export function BehaviorNote({ flags }) {
  const { t } = useI18n();
  if (!flags || !flags.length) return null;
  return (
    <div className="bh-note" role="note">
      <strong>{t('Behavior check')}</strong>
      <ul>
        {flags.map((f) => (
          <li key={f.kind}>
            <span aria-hidden="true">{ICONS[f.kind]}</span> <b>{t(groupOf(f.kind).name)} · {t(patternOf(f.kind).name)}:</b> {f.text}
          </li>
        ))}
      </ul>
    </div>
  );
}

const DISMISSED_KEY = 'mm-tilt-dismissed';
const readDismissed = () => {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]');
  } catch {
    return [];
  }
};

// Live session: the newest trade today that matches a costly pattern, with a timeout button.
// Trades from the last 60 days set what's usual.
export function BehaviorAlert({ trades, accounts, notes, selected, onTimeout }) {
  const { t } = useI18n();
  const [dismissed, setDismissed] = useState(readDismissed);
  const shown = useMemo(() => (selected === 'all' || !Array.isArray(selected) ? trades : trades.filter((x) => selected.includes(x.accountId))), [trades, selected]);
  const latest = useMemo(() => {
    const today = tradingDayKey(new Date());
    const todays = shown.filter((x) => tradingDayKey(x.date) === today);
    if (!todays.length) return null;
    const since = Date.now() - 60 * 86400000;
    const todayNotes = notes && notes[dayKey(new Date())] ? { [dayKey(new Date())]: notes[dayKey(new Date())] } : {};
    const r = detectBehavior(todays, { accounts, notes: todayNotes, baselineTrades: shown.filter((x) => new Date(x.date).getTime() >= since) });
    const alarm = (x) => (r.flags.get(x._id) || []).filter((f) => !patternOf(f.kind).costless);
    const hits = todays.filter((x) => alarm(x).length).sort((a, b) => new Date(b.date) - new Date(a.date));
    return hits.length ? { trade: hits[0], flags: alarm(hits[0]), count: hits.length } : null;
  }, [shown, accounts, notes]);
  useEffect(() => {
    try {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed.slice(-50)));
    } catch {
      // Not remembered in private mode.
    }
  }, [dismissed]);
  if (!latest || dismissed.includes(latest.trade._id)) return null;
  const main = latest.flags[0];
  const g = groupOf(main.kind);
  return (
    <div className="loss-warning tilt-alert" role="alert">
      <strong>{g.face} {t('{behavior} check: {pattern}', { behavior: t(g.name), pattern: t(patternOf(main.kind).name).toLowerCase() })}</strong>{' '}
      <span>{latest.trade.symbol}: {main.text}</span>
      {latest.count > 1 && <span> {t('{n} trades today match a pattern.', { n: latest.count })}</span>}
      <span className="tilt-alert-actions">
        <button type="button" className="small" onClick={() => { onTimeout(10); setDismissed((d) => [...d, latest.trade._id]); }}>{t('Start a 10-minute timeout')}</button>
        <button type="button" className="secondary small" onClick={() => setDismissed((d) => [...d, latest.trade._id])}>{t('It was in my plan')}</button>
      </span>
    </div>
  );
}
